import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, MapPin, Lock, X, Landmark, BookOpen, Feather, Award } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArshHeader } from '../components/ArshHeader';
import { BACKEND_URL } from '../utils/api';
import { ANIMAL_ICONS, EMPIRE_ICON } from '../theme';
import { AuraRing } from '../components/AuraRing';
import { BlissStamp } from '../components/BlissStamp';
import { QuoteBook } from '../components/try/QuoteBook';
import { SignatureReplay } from '../components/try/SignatureReplay';
import { cachedFetch } from '../utils/profileCache';
import { useHistoryClose } from '../hooks/useHistoryClose';

/* MuseumScenePure is heavy (Three.js) — lazy-load so it only downloads
   when the visitor actually taps the Vault button. */
const MuseumLazy = lazy(() => import('../components/museum/MuseumScenePure'));

/* Pronoia balance box — mirrors the identical component on the Empire profile
   (Home.js › PronoiaBalanceBox) so the two surfaces read as one system. */
const PronoiaBalanceBox = ({ pronoia }) => {
  const formatted = Intl.NumberFormat('en-US').format(pronoia || 0);
  const len = formatted.length;
  const fontSize = len >= 13 ? 11 : len >= 11 ? 12 : len >= 9 ? 13 : len >= 7 ? 15 : 17;
  return (
    <div
      className="bliss-world-beam w-full rounded-xl px-2 py-2 flex flex-col items-center justify-center overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at 30% 20%, rgba(197,160,89,0.08) 0%, rgba(0,0,0,0) 60%), ' +
          'linear-gradient(180deg, #0c0710 0%, #050308 100%)',
      }}
      data-testid="pronoia-box"
      aria-label={`Pronoia count ${formatted}`}>
      <p className="font-['Outfit'] uppercase tracking-[0.24em] leading-none"
        style={{ color: '#C5A059', fontSize: 8 }}>
        Pronoia
      </p>
      <p className="font-['Outfit'] font-medium leading-none mt-1"
        style={{
          color: '#FFE9A0',
          fontSize,
          letterSpacing: '0.02em',
          textShadow: '0 0 8px rgba(197,160,89,0.45)',
          whiteSpace: 'nowrap',
        }}
        data-testid="pronoia-count">
        {formatted}
      </p>
    </div>
  );
};

/* Pagination — 10 items per gallery page, infinite-scroll on bottom. */
const PAGE_SIZE = 10;

/* Per-category Polaroid theme (mirrors MemoryGrid.js home grid) — used to
   wrap every memory card on the Discover user-profile feed in the same
   royal-leather frame + accent border + 90/10 caption row. */
const CARD_THEME = {
  fashion: { frame: 'linear-gradient(180deg,#3a2510 0%,#2a1908 100%)', accent: '#D4AF37' },
  food:    { frame: 'linear-gradient(180deg,#322610 0%,#241a08 100%)', accent: '#E8D88C' },
  explore: { frame: 'linear-gradient(180deg,#101e3c 0%,#0a1428 100%)', accent: '#5B8BD8' },
  souls:   { frame: 'linear-gradient(180deg,#3a0e1a 0%,#2a070e 100%)', accent: '#DC143C' },
  sporty:  { frame: 'linear-gradient(180deg,#0e2a1d 0%,#0a1f15 100%)', accent: '#10B981' },
  success: { frame: 'linear-gradient(180deg,#202024 0%,#16161a 100%)', accent: '#D9D9D9' },
  legacy:  { frame: 'linear-gradient(180deg,#221530 0%,#180f24 100%)', accent: '#8B5CF6' },
  art:     { frame: 'linear-gradient(180deg,#0e1530 0%,#080f24 100%)', accent: '#3D8BFF' },
};

const CATEGORIES = ['fashion', 'food', 'explore', 'souls', 'sporty', 'success', 'legacy', 'art'];

/* Six-pillar aura palette — mirrors AuraRing.jsx PILLAR_COLOR, used for the
   6 mini-bars in the compact aura strip on the right side of the header. */
const AURA_PILLARS = [
  { key: 'power',   color: '#FF6478' },
  { key: 'fuel',    color: '#5BE08A' },
  { key: 'force',   color: '#5DD4FF' },
  { key: 'energy',  color: '#B978FF' },
  { key: 'destiny', color: '#7C9BFF' },
  { key: 'aegis',   color: '#FFB265' },
];

/* Eight-pillar vibe palette (matches CATEGORY_STYLE colors) — used for the
   8-segment horizontal vibes line on the right side of the header. */
const VIBE_COLORS = {
  fashion: '#D4AF37',
  food:    '#C8A95E',
  explore: '#0F52BA',
  souls:   '#DC143C',
  sporty:  '#10B981',
  success: '#E5C44D',
  legacy:  '#8B5CF6',
  art:     '#1E6FE3',
};

const CATEGORY_STYLE = {
  fashion: { gradient: 'from-[#D4AF37] to-[#B8926A]', text: '#D4AF37' },
  food: { gradient: 'from-[#D4AF37]/80 to-[#B8926A]', text: '#D4AF37' },
  explore: { gradient: 'from-[#0F52BA] to-[#3B82F6]', text: '#0F52BA' },
  souls: { gradient: 'from-[#D4AF37]/60 to-[#B8926A]/60', text: '#D4AF37' },
  sporty: { gradient: 'from-[#10B981] to-[#059669]', text: '#10B981' },
  success: { gradient: 'from-[#D4AF37] to-[#E5C44D]', text: '#D4AF37' },
  legacy: { gradient: 'from-[#D4AF37]/70 to-[#B8926A]/70', text: '#D4AF37' },
  art: { gradient: 'from-[#1E6FE3] to-[#0F52BA]', text: '#1E6FE3' },
};

const cardAnimation = (i) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { delay: Math.min(i * 0.04, 0.2), duration: 0.25 },
});

export default function UserProfile() {
  const { email } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [error, setError] = useState('');
  const [userStats, setUserStats] = useState({ category_counts: {} });

  /* Paginated gallery state */
  const [page, setPage] = useState(0);            // next page index to fetch
  const [hasMore, setHasMore] = useState(true);   // server returned a full PAGE_SIZE last time
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {url, type, note} for full-screen image view
  const sentinelRef = useRef(null);

  /* Read-only vault + royal-quotes + sign + stamp modals */
  const [showVault, setShowVault] = useState(false);
  const [showQuotes, setShowQuotes] = useState(false);
  const [showSign, setShowSign] = useState(false);
  const [showStamp, setShowStamp] = useState(false);
  const [vaultItems, setVaultItems] = useState([]);
  const [royalQuotes, setRoyalQuotes] = useState([]);

  // Hardware/browser back closes category filter first, then lightbox,
  // then walks React Router history to previous page.
  useHistoryClose(!!lightbox, useCallback(() => setLightbox(null), []));
  useHistoryClose(!!activeCategory && !lightbox, useCallback(() => setActiveCategory(null), []));
  useHistoryClose(showVault, useCallback(() => setShowVault(false), []));
  useHistoryClose(showQuotes, useCallback(() => setShowQuotes(false), []));
  useHistoryClose(showSign, useCallback(() => setShowSign(false), []));
  useHistoryClose(showStamp, useCallback(() => setShowStamp(false), []));

  const loadProfile = useCallback(async () => {
    try {
      const data = await cachedFetch(`${BACKEND_URL}/api/users/${email}/profile`);
      if (data && data.email) setProfile(data);
      else setError('User not found');
    } catch (e) {
      if (e.response?.status === 404) setError('User not found');
      else setError('Failed to load profile');
    }
  }, [email]);

  /**
   * Load one page of memories (10 items by default) and append to state.
   * Uses module-level cache so repeated visits within an app session
   * incur zero network calls.  Reset page=0 to start fresh (e.g. when
   * the active category filter changes).
   */
  const loadMemoriesPage = useCallback(async (pageIdx, replace = false) => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const skip = pageIdx * PAGE_SIZE;
      const base = `${BACKEND_URL}/api/users/${email}/memories`;
      const url = activeCategory
        ? `${base}?category=${activeCategory}&skip=${skip}&limit=${PAGE_SIZE}`
        : `${base}?skip=${skip}&limit=${PAGE_SIZE}`;
      const data = await cachedFetch(url);
      const next = Array.isArray(data) ? data : [];
      setHasMore(next.length === PAGE_SIZE);
      setMemories((prev) => (replace ? next : [...prev, ...next]));
    } catch (e) {
      if (e.response?.status === 403) setError('This profile is private');
    } finally {
      setLoadingMore(false);
    }
  }, [email, activeCategory, loadingMore]);

  /* First-page fetch — runs on mount and whenever the category filter
     changes.  Resets the list, hasMore, and page index. */
  const reloadFirstPage = useCallback(async () => {
    setMemories([]);
    setPage(0);
    setHasMore(true);
    await loadMemoriesPage(0, true);
    setPage(1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, activeCategory]);

  const loadStats = useCallback(async () => {
    try {
      const data = await cachedFetch(`${BACKEND_URL}/api/users/${email}/stats`);
      setUserStats(data);
    } catch (_e) { /* stats optional */ }
  }, [email]);

  /* Read-only vault preview (all public memories) and royal-quotes list.
     Populated once on mount so tapping the left-side icons opens instantly.
     Fetches up to 500 memories so the 3D vault museum has the full set. */
  const loadVaultAndQuotes = useCallback(async () => {
    try {
      const vault = await cachedFetch(`${BACKEND_URL}/api/users/${email}/memories?limit=500`);
      setVaultItems(Array.isArray(vault) ? vault : []);
    } catch (_e) { /* private profile — vault stays empty */ }
    try {
      const q = await cachedFetch(`${BACKEND_URL}/api/users/${email}/memories?category=quote&limit=200`);
      setRoyalQuotes(Array.isArray(q) ? q : []);
    } catch (_e) { /* quotes optional */ }
  }, [email]);

  /* Mount: load profile + first page of memories + stats. */
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await loadProfile();
      await reloadFirstPage();
      await loadStats();
      await loadVaultAndQuotes();
      setLoading(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  /* When the category filter flips, reload from page 0. */
  useEffect(() => {
    if (!loading) reloadFirstPage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory]);

  /* Infinite-scroll sentinel — fetches the next page when the user
     scrolls to within 200px of the bottom marker. */
  useEffect(() => {
    if (!hasMore || loading || loadingMore) return;
    const node = sentinelRef.current;
    if (!node) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        loadMemoriesPage(page).then(() => setPage((p) => p + 1));
      }
    }, { rootMargin: '200px' });
    obs.observe(node);
    return () => obs.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading, loadingMore, page, activeCategory]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#000000' }}>
        <div className="w-16 h-16 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#000000' }}>
        <Lock size={48} className="text-white/20" />
        <p className="font-['Cormorant_Garamond'] text-xl text-white">{error || 'User not found'}</p>
        <button onClick={() => navigate('/discover')} className="px-6 py-2 border border-[#D4AF37]/30 text-[#D4AF37] rounded-full font-['Outfit'] text-sm">Back to Discover</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: '#000000' }}>
      <style>{`
        @keyframes tagFlicker {
          0%, 100% { box-shadow: 0 0 4px var(--tag-color); filter: brightness(0.85); }
          50% { box-shadow: 0 0 14px var(--tag-color), 0 0 28px var(--tag-color); filter: brightness(1.2); }
        }
        .tag-flicker { animation: tagFlicker 2s ease-in-out infinite; }
        /* Breathing 8-colour beam — mirrors Home.js so the Gems box pulses
           through all 8 pillar colours exactly like the main Empire profile. */
        @keyframes blissWorldBeam {
          0%   { border-color: #D4AF37; box-shadow: 0 0 12px rgba(212,175,55,0.55), inset 0 0 8px rgba(212,175,55,0.10); }
          12.5%{ border-color: #C8A95E; box-shadow: 0 0 12px rgba(200,169,94,0.55), inset 0 0 8px rgba(200,169,94,0.10); }
          25%  { border-color: #0F52BA; box-shadow: 0 0 14px rgba(15,82,186,0.60),  inset 0 0 8px rgba(15,82,186,0.10); }
          37.5%{ border-color: #DC143C; box-shadow: 0 0 14px rgba(220,20,60,0.60),  inset 0 0 8px rgba(220,20,60,0.10); }
          50%  { border-color: #10B981; box-shadow: 0 0 14px rgba(16,185,129,0.60), inset 0 0 8px rgba(16,185,129,0.10); }
          62.5%{ border-color: #E5C44D; box-shadow: 0 0 12px rgba(229,196,77,0.55), inset 0 0 8px rgba(229,196,77,0.10); }
          75%  { border-color: #8B5CF6; box-shadow: 0 0 14px rgba(139,92,246,0.60), inset 0 0 8px rgba(139,92,246,0.10); }
          87.5%{ border-color: #2A9BD8; box-shadow: 0 0 14px rgba(42,155,216,0.60), inset 0 0 8px rgba(42,155,216,0.10); }
          100% { border-color: #D4AF37; box-shadow: 0 0 12px rgba(212,175,55,0.55), inset 0 0 8px rgba(212,175,55,0.10); }
        }
        .bliss-world-beam {
          border: 2px solid #D4AF37;
          animation: blissWorldBeam 12s linear infinite;
        }
      `}</style>
      {/* Header */}
      <div className="border-b border-[#D4AF37]/10" style={{ background: 'linear-gradient(180deg, #0a0a14 0%, #05050A 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 pt-2 pb-6">
          <ArshHeader />
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/30 hover:text-[#D4AF37] mb-4 font-['Outfit'] text-sm transition-colors" data-testid="back-button">
            <ArrowLeft size={18} />Back
          </button>

          <div className="flex gap-2">
            {/* ─── LEFT: 4 buttons stacked vertically — Sign · Stamp · Vault · Royal Quotes ─── */}
            <div className="flex flex-col items-center gap-2 w-[80px] flex-shrink-0" data-testid="user-left-column">
              {/* Sign — CYAN accent */}
              <button
                type="button"
                onClick={() => setShowSign(true)}
                className="w-full active:scale-95 transition-transform"
                data-testid="open-sign-btn"
                aria-label="View signature">
                <div
                  className="w-full rounded-md flex flex-col items-center justify-center gap-[2px] py-1.5"
                  style={{
                    background: 'linear-gradient(180deg,#0e1428 0%,#050a18 100%)',
                    border: '1.5px solid #5DD4FF',
                    boxShadow: '0 0 10px rgba(93,212,255,0.35), inset 0 0 6px rgba(93,212,255,0.18)',
                  }}>
                  <Feather size={16} style={{ color: '#8FE4FF' }} />
                  <span className="font-['Cinzel'] text-[8px] uppercase tracking-[0.2em] leading-none"
                    style={{ color: '#A8EBFF' }}>
                    Sign
                  </span>
                </div>
              </button>

              {/* Stamp — GOLD accent */}
              <button
                type="button"
                onClick={() => setShowStamp(true)}
                className="w-full active:scale-95 transition-transform"
                data-testid="open-stamp-btn"
                aria-label="View stamp">
                <div
                  className="w-full rounded-md flex flex-col items-center justify-center gap-[2px] py-1.5"
                  style={{
                    background: 'linear-gradient(180deg,#1a1408 0%,#0a0800 100%)',
                    border: '1.5px solid #D4AF37',
                    boxShadow: '0 0 10px rgba(212,175,55,0.35), inset 0 0 6px rgba(212,175,55,0.18)',
                  }}>
                  <Award size={16} style={{ color: '#F5D97A' }} />
                  <span className="font-['Cinzel'] text-[8px] uppercase tracking-[0.2em] leading-none"
                    style={{ color: '#F5D97A' }}>
                    Stamp
                  </span>
                </div>
              </button>

              {/* Vault — RED accent */}
              <button
                type="button"
                onClick={() => setShowVault(true)}
                className="w-full active:scale-95 transition-transform"
                data-testid="open-vault-btn"
                aria-label="Vault">
                <div
                  className="w-full rounded-md flex flex-col items-center justify-center gap-[2px] py-1.5"
                  style={{
                    background: 'linear-gradient(180deg,#1a0407 0%,#0a0102 100%)',
                    border: '1.5px solid #DC143C',
                    boxShadow: '0 0 10px rgba(220,20,60,0.35), inset 0 0 6px rgba(220,20,60,0.18)',
                  }}>
                  <Landmark size={16} style={{ color: '#FF5A6E' }} />
                  <span className="font-['Cinzel'] text-[8px] uppercase tracking-[0.2em] leading-none"
                    style={{ color: '#FF7A88' }}>
                    Vault
                  </span>
                </div>
              </button>

              {/* Royal Quotes — PURPLE accent */}
              <button
                type="button"
                onClick={() => setShowQuotes(true)}
                className="w-full active:scale-95 transition-transform"
                data-testid="open-quotes-btn"
                aria-label="Royal Quotes">
                <div
                  className="w-full rounded-md flex flex-col items-center justify-center gap-[2px] py-1.5"
                  style={{
                    background: 'linear-gradient(180deg,#160a1e 0%,#0a0410 100%)',
                    border: '1.5px solid #8B5CF6',
                    boxShadow: '0 0 10px rgba(139,92,246,0.35), inset 0 0 6px rgba(139,92,246,0.18)',
                  }}>
                  <BookOpen size={16} style={{ color: '#B18CFF' }} />
                  <span className="font-['Cinzel'] text-[8px] uppercase tracking-[0.2em] text-center leading-none"
                    style={{ color: '#C6ACFF' }}>
                    Royal<br />Quotes
                  </span>
                </div>
              </button>
            </div>

            {/* ─── CENTER PROFILE — untouched (tag moved to right column top) ─── */}
            <div className="flex-1 flex flex-col items-center text-center min-w-0">
              <div className="mb-3" style={{ width: 112, height: 112 }}>
                <AuraRing size={112} data={profile.aura_ring || { fused: {}, gold: false }}>
                  <div className="w-24 h-24 rounded-full border-2 border-[#D4AF37]/30 overflow-hidden" style={{ boxShadow: '0 0 20px rgba(212,175,55,0.15)' }}>
                    {profile.profile_picture ? (
                      <img src={profile.profile_picture} alt={profile.nickname} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-[#05050A] flex items-center justify-center">
                        <span className="font-['Cormorant_Garamond'] text-3xl text-[#D4AF37]">{profile.nickname?.charAt(0).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                </AuraRing>
              </div>
              <h1 className="font-['Cormorant_Garamond'] text-2xl font-light text-white tracking-wide" data-testid="user-profile-name">{profile.nickname}</h1>
              {profile.life_quote && <p className="font-['Cormorant_Garamond'] italic text-white/30 mt-1 text-sm">{'"'}{profile.life_quote}{'"'}</p>}
            </div>

            {/* ─── RIGHT: profile-tag icon (top) + Aura + Vibes + Gems strip ─── */}
            <div className="flex flex-col gap-2 w-[110px] flex-shrink-0" data-testid="user-right-strip">
              {/* Profile tag — flickering animal icon, centered above AURA line */}
              {profile.profile_tag && ANIMAL_ICONS[profile.profile_tag] && (
                <div className="flex justify-center mb-1" data-testid="right-profile-tag">
                  <div className="tag-flicker w-9 h-9 rounded-full overflow-hidden"
                    style={{
                      '--tag-color': ({ fashion: '#D4AF37', food: '#C8A95E', explore: '#0F52BA', souls: '#DC143C', sporty: '#10B981', success: '#E5C44D', legacy: '#8B5CF6' }[profile.profile_tag] || '#D4AF37') + '80',
                      border: `2px solid ${{ fashion: '#D4AF37', food: '#C8A95E', explore: '#0F52BA', souls: '#DC143C', sporty: '#10B981', success: '#E5C44D', legacy: '#8B5CF6' }[profile.profile_tag] || '#D4AF37'}60`,
                    }}>
                    <img src={ANIMAL_ICONS[profile.profile_tag].src} alt="" className="w-full h-full object-cover" style={{ objectPosition: ANIMAL_ICONS[profile.profile_tag].pos }} />
                  </div>
                </div>
              )}
              {/* AURA — top gold/silver line + 6 mini aura bars */}
              <div>
                <div className="font-['Cinzel'] text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]/80 mb-1">
                  Aura
                </div>
                {/* Top: single gold/silver line indicating today's status */}
                <div
                  className="h-[5px] rounded-full mb-1"
                  style={{
                    background: profile.aura_ring?.gold
                      ? 'linear-gradient(90deg,#FFD86B,#F5C242,#FFD86B)'
                      : 'linear-gradient(90deg,#C6C6C6,#EDEDED,#C6C6C6)',
                    boxShadow: profile.aura_ring?.gold
                      ? '0 0 6px rgba(255,216,107,0.55)'
                      : '0 0 4px rgba(230,230,230,0.35)',
                  }}
                  data-testid="aura-top-line" />
                {/* Bottom: 6 tiny bars in one horizontal row */}
                <div className="flex gap-[3px]" data-testid="aura-six-bars">
                  {AURA_PILLARS.map((p) => {
                    const isFused = !!profile.aura_ring?.fused?.[p.key];
                    return (
                      <div
                        key={p.key}
                        className="flex-1 h-[9px] rounded-[2px]"
                        style={{
                          background: isFused ? p.color : `${p.color}22`,
                          border: `1px solid ${isFused ? p.color : `${p.color}44`}`,
                          boxShadow: isFused ? `0 0 5px ${p.color}88` : 'none',
                        }}
                        title={p.key}
                      />
                    );
                  })}
                </div>
              </div>

              {/* VIBES — label + inline count box, 8-icon coloured horizontal line below */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-['Cinzel'] text-[9px] uppercase tracking-[0.18em] text-[#D4AF37]/80">
                    Vibes
                  </span>
                  <div
                    className="px-2 py-[1px] rounded-[3px] flex items-center justify-center min-w-[26px]"
                    style={{
                      background: 'linear-gradient(180deg,#1a1204 0%,#0a0700 100%)',
                      border: '1px solid rgba(212,175,55,0.5)',
                      boxShadow: 'inset 0 0 4px rgba(212,175,55,0.2)',
                    }}
                    data-testid="vibes-count">
                    <span className="font-['Cinzel'] text-[11px] font-semibold text-[#F5C242] leading-none">
                      {Object.values(userStats.category_counts || {}).reduce((a, b) => a + (b || 0), 0)}
                    </span>
                  </div>
                </div>
                <div
                  className="flex h-[10px] rounded-[3px] overflow-hidden"
                  style={{ border: '1px solid rgba(212,175,55,0.35)' }}
                  data-testid="vibes-eight-line">
                  {CATEGORIES.map((cat) => {
                    const count = userStats.today_counts?.[cat] || 0;
                    const on = count > 0;
                    const color = VIBE_COLORS[cat];
                    return (
                      <div
                        key={cat}
                        className="flex-1 h-full"
                        style={{
                          background: on ? color : `${color}20`,
                          boxShadow: on ? `inset 0 0 4px ${color}` : 'none',
                        }}
                        title={`${cat}: ${count} today`}
                        data-testid={`vibes-seg-${cat}`}
                      />
                    );
                  })}
                </div>
              </div>

              {/* PRONOIA — identical component to Empire profile PronoiaBalanceBox, pushed down toward the bottom of the profile block */}
              <div className="mt-8">
                <PronoiaBalanceBox pronoia={profile.pronoia} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category filter — Icon-based */}
      <div className="max-w-4xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Dragon = All */}
          <button onClick={() => setActiveCategory(null)}
            className="flex flex-col items-center gap-1"
            data-testid="filter-all">
            <div className="w-9 h-9 rounded-full overflow-hidden transition-all duration-300"
              style={{
                border: !activeCategory ? '2px solid rgba(212,175,55,0.7)' : '1.5px solid rgba(255,255,255,0.1)',
                boxShadow: !activeCategory ? '0 0 14px rgba(212,175,55,0.4)' : 'none',
                opacity: !activeCategory ? 1 : 0.45,
              }}>
              <img src={EMPIRE_ICON.src} alt="All" className="w-full h-full object-cover" style={{ objectPosition: EMPIRE_ICON.pos }} />
            </div>
          </button>
          {CATEGORIES.map((cat) => {
            const icon = ANIMAL_ICONS[cat];
            const isActive = activeCategory === cat;
            const catColor = CATEGORY_STYLE[cat]?.text || '#D4AF37';
            return (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className="flex flex-col items-center gap-1"
                data-testid={`filter-${cat}`}>
                <div className="w-9 h-9 rounded-full overflow-hidden transition-all duration-300"
                  style={{
                    border: isActive ? `2px solid ${catColor}` : '1.5px solid rgba(255,255,255,0.1)',
                    boxShadow: isActive ? `0 0 14px ${catColor}60` : 'none',
                    opacity: isActive ? 1 : 0.45,
                  }}>
                  {icon && <img src={icon.src} alt={cat} className="w-full h-full object-cover" style={{ objectPosition: icon.pos }} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Vibes feed */}
      <div className="max-w-4xl mx-auto px-4">
        {memories.length > 0 ? (
          <div className="space-y-5">
            {memories.map((mem, i) => {
              // Prefer the lightweight thumbnail in the grid view; fall back
              // to image_url for legacy memories.
              const previewUrl = mem.thumb_url || mem.image_url;
              const theme = CARD_THEME[mem.category] || CARD_THEME.art;
              return (
                <motion.div key={mem.id || i} {...cardAnimation(i)}
                  className="relative overflow-hidden"
                  style={{
                    background: theme.frame,
                    border: `1.5px solid ${theme.accent}`,
                    borderRadius: 8,
                    padding: '12px 12px 14px',
                    boxShadow:
                      `inset 0 0 0 1px ${theme.accent}55,` +
                      `0 0 14px ${theme.accent}44,` +
                      `0 10px 22px rgba(0,0,0,0.6)`,
                  }}
                  data-testid={`user-memory-${i}`}>
                  {/* Image well — recessed inside the gold-trimmed mat */}
                  <div className="relative overflow-hidden" style={{
                    borderRadius: 4,
                    aspectRatio: '4 / 5',
                    background: '#05050A',
                    boxShadow: `inset 0 0 0 1px ${theme.accent}aa, inset 0 0 12px rgba(0,0,0,0.6)`,
                  }}>
                    {mem.media_type === 'video' ? (
                      <video src={mem.image_url} className="w-full h-full object-cover" controls />
                    ) : mem.media_type === 'audio' ? (
                      <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/5 to-[#05050A] flex flex-col items-center justify-center gap-3 px-6">
                        <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>
                        </div>
                        <audio src={mem.image_url} controls className="w-full max-w-xs" />
                      </div>
                    ) : (
                      <button type="button"
                        onClick={() => setLightbox({ url: mem.image_url, note: mem.note })}
                        className="block w-full h-full"
                        data-testid={`memory-fullscreen-${i}`}>
                        <img src={previewUrl} alt={mem.note}
                          className="w-full h-full object-cover cursor-zoom-in"
                          loading="lazy" decoding="async"/>
                      </button>
                    )}
                    {/* Animal icon badge — bottom-right corner */}
                    {ANIMAL_ICONS[mem.category] && (
                      <div className="absolute bottom-2 right-2 z-[5] pointer-events-none">
                        <div className="w-7 h-7 rounded-full overflow-hidden border"
                          style={{ borderColor: theme.accent, boxShadow: `0 0 10px ${theme.accent}88` }}>
                          <img src={ANIMAL_ICONS[mem.category].src} alt={mem.category}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: ANIMAL_ICONS[mem.category].pos }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Caption band — 90% note · 10% date */}
                  <div className="mt-2.5 flex items-stretch gap-2"
                    style={{
                      minHeight: 56,
                      borderTop: `1px solid ${theme.accent}55`,
                      paddingTop: 8,
                    }}>
                    <div className="flex-1 min-w-0 flex items-center" style={{ flexBasis: '90%' }}>
                      {mem.note ? (
                        <p className="font-['Cormorant_Garamond'] italic leading-snug line-clamp-3 w-full"
                          style={{ color: '#F5E6A0', fontSize: 14 }}>
                          {mem.note}
                        </p>
                      ) : (
                        <p className="font-['Cormorant_Garamond'] italic leading-snug w-full"
                          style={{ color: 'rgba(245,217,122,0.35)', fontSize: 14 }}>
                          — no note —
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-center justify-center px-1.5 text-center"
                      style={{
                        flexBasis: '10%',
                        minWidth: 44,
                        borderLeft: `1px solid ${theme.accent}55`,
                      }}>
                      {(() => {
                        const d = mem.created_at ? new Date(mem.created_at) : null;
                        if (!d || isNaN(d.getTime())) return (
                          <span className="font-['Outfit'] uppercase" style={{ color: `${theme.accent}80`, fontSize: 9 }}>—</span>
                        );
                        const day   = d.getDate();
                        const month = d.toLocaleString('en-US', { month: 'short' }).toUpperCase();
                        const year  = String(d.getFullYear()).slice(-2);
                        return (
                          <>
                            <span className="font-['Cormorant_Garamond'] font-bold leading-none"
                              style={{ color: theme.accent, fontSize: 18 }}>{day}</span>
                            <span className="font-['Outfit'] uppercase tracking-[0.18em] leading-none mt-0.5"
                              style={{ color: `${theme.accent}cc`, fontSize: 8 }}>{month}</span>
                            <span className="font-['Outfit'] tracking-[0.16em] leading-none mt-0.5"
                              style={{ color: `${theme.accent}88`, fontSize: 7.5 }}>’{year}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {/* Infinite-scroll sentinel — IntersectionObserver triggers the
               next page when this enters the viewport (rootMargin 200px). */}
            {hasMore && (
              <div ref={sentinelRef}
                className="flex items-center justify-center py-6"
                data-testid="memories-load-more-sentinel">
                {loadingMore ? (
                  <div className="flex items-center gap-2 font-['Outfit'] text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]/70">
                    <div className="w-4 h-4 rounded-full border-2 border-[#D4AF37]/30 border-t-[#D4AF37] animate-spin"/>
                    Loading more vibes…
                  </div>
                ) : (
                  <span className="font-['Outfit'] text-[10px] tracking-[0.3em] uppercase text-white/20">
                    Scroll for more
                  </span>
                )}
              </div>
            )}
            {!hasMore && memories.length > PAGE_SIZE && (
              <div className="text-center py-6">
                <span className="font-['Cormorant_Garamond'] italic text-[12px] text-[#D4AF37]/40">
                  ✦ End of the codex ✦
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20">
            <p className="font-['Outfit'] text-white/30">No vibes to show yet</p>
          </div>
        )}
      </div>

      {/* Full-screen lightbox — loads the high-resolution image only
         when the user explicitly taps a thumbnail.  Tap-anywhere or
         the X button closes. */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
            onClick={() => setLightbox(null)}
            data-testid="memory-lightbox">
            <button type="button"
              onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center"
              style={{ border: '1px solid rgba(212,175,55,0.4)', color: '#D4AF37', background: 'rgba(0,0,0,0.4)' }}
              data-testid="memory-lightbox-close">
              <X size={18}/>
            </button>
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
              src={lightbox.url}
              alt={lightbox.note || ''}
              className="max-w-full max-h-full object-contain rounded-xl"
              style={{ boxShadow: '0 0 60px rgba(212,175,55,0.15)' }}
              onClick={(e) => e.stopPropagation()}
              data-testid="memory-lightbox-image"
            />
            {lightbox.note && (
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 max-w-[90%]">
                <p className="font-['Cormorant_Garamond'] italic text-center text-white/80 text-sm px-4 py-2 rounded-lg"
                  style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.2)' }}>
                  {lightbox.note}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Read-only VAULT — full 3D museum with the same 8 doors as the owner
          sees. The museum's built-in dragon-exit calls onExit → closes vault. */}
      {showVault && (
        <div className="fixed inset-0 z-[190]" style={{ background: '#04030a' }}
          data-testid="vault-modal">
          <Suspense fallback={
            <div className="fixed inset-0 flex items-center justify-center"
              style={{ background: '#04030a', color: '#F5D97A', fontFamily: 'Cormorant Garamond, serif' }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                border: '2px solid #D4AF37', borderTopColor: 'transparent',
                animation: 'museumSpin 0.8s linear infinite',
              }} />
              <style>{`@keyframes museumSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          }>
            <MuseumLazy
              onExit={() => setShowVault(false)}
              initialRoomId="hall"
              publicData={{
                user: { nickname: profile.nickname, email: profile.email, profile_picture: profile.profile_picture },
                items: vaultItems,
                meta: { label: `${profile.nickname || 'Sovereign'}\u2019s Vault` },
              }}
            />
          </Suspense>
        </div>
      )}

      {/* SIGN — full-screen signature replay of the visited user */}
      {showSign && (
        <>
          {(profile.signature_strokes || profile.signature_data_url) ? (
            <SignatureReplay
              strokes={profile.signature_strokes}
              fallbackImage={profile.signature_data_url}
              onClose={() => setShowSign(false)}
            />
          ) : (
            <div
              className="fixed inset-0 z-[200] flex flex-col items-center justify-center px-8"
              style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
              onClick={() => setShowSign(false)}
              data-testid="sign-empty-modal">
              <p className="font-['Cormorant_Garamond'] italic text-white/60 text-xl mb-3 text-center">
                no signature sealed yet
              </p>
              <p className="font-['Outfit'] text-[11px] tracking-[0.24em] uppercase text-white/30">
                tap anywhere to close
              </p>
            </div>
          )}
        </>
      )}

      {/* STAMP — full-size Essence stamp modal for the visited user */}
      {showStamp && (
        <div
          className="fixed inset-0 z-[200] flex flex-col"
          style={{ background: '#04030a' }}
          data-testid="stamp-modal">
          {/* Header — back arrow (left) + centred "BLISS STAMP" title */}
          <div className="flex items-center justify-between px-4 flex-shrink-0"
            style={{
              paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 14px)',
              paddingBottom: 12,
              borderBottom: '1px solid rgba(212,175,55,0.2)',
              background: 'linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.6))',
            }}>
            <button
              type="button"
              onClick={() => setShowStamp(false)}
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ border: '1px solid rgba(212,175,55,0.4)', color: '#D4AF37', background: 'rgba(0,0,0,0.4)' }}
              aria-label="Back"
              data-testid="stamp-modal-close">
              <ArrowLeft size={18} />
            </button>
            <p className="font-['Cinzel'] uppercase tracking-[0.32em] text-center"
              style={{ color: '#F5D97A', fontSize: 13, textShadow: '0 0 10px rgba(212,175,55,0.4)' }}>
              Bliss Stamp
            </p>
            <div className="w-10" />
          </div>

          {/* Centered stamp on parchment-dark backdrop */}
          <div className="flex-1 flex items-center justify-center px-6 py-8 overflow-auto">
            <div style={{ width: 320, maxWidth: '80vw' }}>
              <BlissStamp
                user={profile}
                totalUploads={Object.values(userStats.category_counts || {}).reduce((a, b) => a + (b || 0), 0)}
                maxWidth={320}
              />
            </div>
          </div>
        </div>
      )}

      {/* Read-only ROYAL BOOK — reuses the owner's QuoteBook UI in read-only
          mode so visitors flip through the exact same leather-bound pages. */}
      {showQuotes && (
        <div data-testid="quotes-modal">
          <QuoteBook
            onClose={() => setShowQuotes(false)}
            readOnlyData={{
              quotes: royalQuotes,
              ownerName: profile.nickname || profile.email?.split('@')[0] || 'Sovereign',
              ownerProfilePicture: profile.profile_picture,
            }}
          />
        </div>
      )}
    </div>
  );
}
